import { Box, Typography, Paper } from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";

export default function PrivacyPolicy() {
  return (
    <Box
      id="privacy-policy"
      sx={{
        py: 10,
        px: 3,
        backgroundColor: "#f9fafb",
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6, }}>
        <ShieldIcon sx={{ color: "#2E5E3A", fontSize: 40 }} />
        <Typography
          variant="h4"
          sx={{ mt: 1, color: "text.primary", fontWeight: 600 }}
        >
          Privacy Policy
        </Typography>
      </Box>

      {/* Content Box */}
      <Paper
        elevation={2}
        sx={{
          maxWidth: "800px",
          mx: "auto",
          p: 5,
          borderRadius: 4,
          backgroundColor: "#fff",
        }}
      >
        <Typography sx={{ color: "text.secondary", mb: 3 }}>
          Spendifyr is an expense management application designed to help users
          track and manage their personal finances. This Privacy Policy explains
          how we collect, use, store, and protect your information when you use
          the Spendifyr mobile application. By using Spendifyr, you agree to the
          practices described in this policy.
        </Typography>

        {/* 1. Information We Collect */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            1. Information We Collect
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 1, fontWeight: 500 }}>
            1.1 Information You Provide
          </Typography>
          <Typography component="div" sx={{ color: "text.secondary", mb: 2 }}>
            <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
              <li>Name</li>
              <li>Email address</li>
              <li>Profile picture</li>
              <li>Expense records (e.g., categories, amounts, notes)</li>
              <li>Any data you manually enter in the app</li>
            </ul>
          </Typography>

          <Typography sx={{ color: "text.secondary", mb: 1, fontWeight: 500 }}>
            1.2 Automatically Collected Information
          </Typography>
          <Typography component="div" sx={{ color: "text.secondary", mb: 2 }}>
            When you use Spendifyr, we may collect:
            <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
              <li>Device information (model, OS version)</li>
              <li>App usage information</li>
              <li>Crash logs and performance data</li>
            </ul>
          </Typography>

          <Typography sx={{ color: "text.secondary", mb: 1, fontWeight: 500 }}>
            1.3 Firebase Information
          </Typography>
          <Typography component="div" sx={{ color: "text.secondary" }}>
            As Spendifyr uses Firebase services, the following Firebase data may
            be collected depending on your usage:
            <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
              <li>Authentication data</li>
              <li>Firestore database records</li>
              <li>Analytics events (if enabled)</li>
            </ul>
            All Firebase data collection complies with Google’s privacy
            standards.
          </Typography>
        </Box>

        {/* 2. How We Use Your Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            2. How We Use Your Information
          </Typography>
          <Typography component="div" sx={{ color: "text.secondary" }}>
            We use your information for the following purposes:
            <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
              <li>To create and manage your Spendifyr account</li>
              <li>To store and sync your expenses securely</li>
              <li>To improve app performance and user experience</li>
              <li>To analyze app usage and fix issues</li>
              <li>To provide customer support</li>
              <li>To maintain the security and integrity of the service</li>
            </ul>
            We do not sell your personal data to any third party.
          </Typography>
        </Box>

        {/* 3. Data Storage & Security */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            3. Data Storage & Security
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            Your data is stored securely using Google Firebase. All data is
            transmitted over SSL encrypted connections. We implement standard
            security practices to prevent unauthorized access. Sensitive data
            such as passwords is managed securely through Firebase
            Authentication. However, no method of data transfer or storage is
            100% secure, and we cannot guarantee absolute protection.
          </Typography>
        </Box>

        {/* 4. Third-Party Services */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            4. Third-Party Services
          </Typography>
          <Typography component="div" sx={{ color: "text.secondary" }}>
            Spendifyr uses the following third-party services:
            <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
              <li>Firebase Authentication</li>
              <li>Firebase Firestore</li>
              <li>Firebase Crashlytics</li>
            </ul>
            These services may collect information as governed by Google’s
            Privacy Policy:{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              https://policies.google.com/privacy
            </a>
          </Typography>
        </Box>

        {/* 5. Data Sharing */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            5. Data Sharing
          </Typography>
          <Typography component="div" sx={{ color: "text.secondary" }}>
            We do not share your data with any person or company except:
            <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
              <li>To comply with legal obligations</li>
              <li>To prevent fraud, abuse, or security issues</li>
              <li>When required by applicable law enforcement agencies</li>
              <li>
                With service providers that help us operate (e.g., Firebase),
                under strict confidentiality
              </li>
            </ul>
            We do not sell your data under any circumstances.
          </Typography>
        </Box>

        {/* 6. Your Rights */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            6. Your Rights
          </Typography>
          <Typography component="div" sx={{ color: "text.secondary" }}>
            Depending on your region, you may have the right to:
            <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
              <li>Access your data</li>
              <li>Update or correct your data</li>
              <li>Delete your account and associated data</li>
              <li>Request a copy of your personal information</li>
              <li>Withdraw consent for data collection</li>
            </ul>
            To exercise these rights, contact us at:{" "}
            <strong>support@spendifyr.com</strong>.
          </Typography>
        </Box>

        {/* 7. Children’s Privacy */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            7. Children’s Privacy
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            Spendifyr is not intended for children under 13. We do not knowingly
            collect personal information from children. If you believe a child
            has provided us data, contact us immediately.
          </Typography>
        </Box>

        {/* 8. Data Retention */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            8. Data Retention
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            We retain your data as long as your Spendifyr account is active.
            When you delete your account, we permanently delete all associated
            data from Firebase, except where retention is required for legal
            purposes.
          </Typography>
        </Box>

        {/* 9. Changes to This Policy */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            9. Changes to This Policy
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            We may update this Privacy Policy occasionally. When we do, the
            updated version will be posted with a new “Last updated” date. We
            recommend reviewing it regularly.
          </Typography>
        </Box>

        {/* 10. Contact Us */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            10. Contact Us
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            If you have questions or concerns about this Privacy Policy, you may
            contact us at:
            <br />
            <br />
            Email: <strong>support@spendifyr.com</strong>
            <br />
            App Name: <strong>Spendifyr</strong>
          </Typography>
        </Box>

        {/* Footer Note */}
        <Typography
          sx={{ color: "text.disabled", fontSize: "0.85rem", pt: 3 }}
        >
          Last updated: December 17, 2025
        </Typography>
      </Paper>
    </Box>
  );
}
